import React, { useContext, useState } from 'react';
import { Card, Col, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import TextAreaComponent from '@/components/TextArea';
import ButtonComponent from '@/components/Button';
import { SmsManagementService } from '@/services';
import { ReactComponent as SentIcon } from '@/assets/images/icons/send.svg';
import { ReactComponent as ChevronRightIcon } from '@/assets/images/icons/chevron-right.svg';
import defaultAvatar from '@/assets/images/default-avatar.png';
import { QUICK_SEND_MAX_CHARACTERS } from '@/constants';
import InputPhone from '@/components/InputPhone';
import { validatePhoneNumber } from '@/utils/validate';
import { GlobalContext } from '@/contexts/GlobalContext';
import SelectFetching from '@/components/SelectFetching';

import './quick-send.scss';
import { momentLocale } from '@/utils/formatter';
import { getCurrentCountry } from '@/utils/domain';
import { getCountryCode } from '@/utils/countryCode';
import { shouldHideSender } from '@/utils/hideSender';

function QuickSend() {
  const { t } = useTranslation();
  const [formInstance] = Form.useForm();
  const [preview, setPreview] = useState({
    senderName: null,
    msg: null,
  });

  // get the current Country
  const country = getCurrentCountry();

  // get the current Country code
  const countryCode = getCountryCode(country);
  const [loadingSubmitForm, setLoadingSubmitForm] = useState(false);

  const fetchSenderList = async (name) => {
    const res = await SmsManagementService.fetchActiveSenderList({ name });

    if (res.success) {
      return res.data.map((sender) => ({
        label: sender.senderName,
        value: sender.id,
      }));
    }

    return [];
  };

  const { setTriggerFetchProfile } = useContext(GlobalContext);

  const handleOnFormFilterFinish = async (values) => {
    setLoadingSubmitForm(true);

    const resp = await SmsManagementService.sendSms({
      ...values,
      senderId: shouldHideSender(country) ? undefined : values.senderId?.value,
      toPhoneNumber: `${countryCode}${values.toPhoneNumber}`,
    });

    if (resp.success) {
      formInstance.resetFields();
      setPreview({
        senderName: null,
        msg: null,
      });
      // Trigger call api get profile to update available sms credit
      setTriggerFetchProfile(true);
    }
    setLoadingSubmitForm(false);
  };

  const handleOnChangeSender = (option) => {
    setPreview({
      ...preview,
      senderName: option?.label,
    });
  };

  const handleOnChangeMsg = (e) => {
    const { value } = e.target;
    setPreview({
      ...preview,
      msg: value,
    });
  };

  return (
    <div className="quick-send-container">
      <Card title={t('sms_page.quick_send.title')} bordered={false}>
        <Row gutter={[48, 16]}>
          <Col span={12}>
            <Form
              layout="vertical"
              onFinish={handleOnFormFilterFinish}
              autoComplete="off"
              className="form"
              form={formInstance}
            >
              <Form.Item
                label={t('sms_page.quick_send.form.receiver.label')}
                name="toPhoneNumber"
                className="form-item"
                rules={[
                  {
                    required: true,
                    message: t(
                      'sms_page.quick_send.form.receiver.required_msg',
                    ),
                  },
                  () => ({
                    validator(_, value) {
                      if (value && !validatePhoneNumber(countryCode, value)) {
                        return Promise.reject(
                          new Error(
                            t('sms_page.quick_send.form.receiver.invalid'),
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputPhone
                  placeholder={t(
                    'sms_page.quick_send.form.receiver.placeholder',
                  )}
                />
              </Form.Item>

              <Form.Item
                hidden={shouldHideSender(country)}
                label={t('sms_page.quick_send.form.sender_name.label')}
                name="senderId"
                className="form-item"
                rules={[
                  {
                    required: !shouldHideSender(country),
                    message: t(
                      'sms_page.quick_send.form.sender_name.required_msg',
                    ),
                  },
                ]}
              >
                <SelectFetching
                  allowClear
                  triggerFetching
                  placeholder={t(
                    'sms_page.quick_send.form.sender_name.placeholder',
                  )}
                  fetchOptions={fetchSenderList}
                  onChange={handleOnChangeSender}
                />
              </Form.Item>

              <Form.Item
                label={t('sms_page.quick_send.form.message_content.label')}
                name="content"
                rules={[
                  {
                    required: true,
                    message: t(
                      'sms_page.quick_send.form.message_content.required_msg',
                    ),
                  },
                  {
                    max: QUICK_SEND_MAX_CHARACTERS,
                    message: t(
                      'sms_page.quick_send.form.message_content.message_max_length',
                      {
                        max: QUICK_SEND_MAX_CHARACTERS,
                      },
                    ),
                  },
                ]}
              >
                <TextAreaComponent
                  placeholder={t(
                    'sms_page.quick_send.form.message_content.placeholder',
                  )}
                  rows={3}
                  showSmsCount
                  onChange={handleOnChangeMsg}
                />
              </Form.Item>

              <Form.Item>
                <div className="form-actions-item">
                  <ButtonComponent
                    type="primary"
                    htmlType="submit"
                    icon={<SentIcon />}
                    loading={loadingSubmitForm}
                    disabled={loadingSubmitForm}
                  >
                    {t('sms_page.quick_send.form.btn_send')}
                  </ButtonComponent>
                </div>
              </Form.Item>
            </Form>
          </Col>
          <Col span={12}>
            <div className="preview-container">
              <div className="iphone">
                <div className="avatar">
                  <img src={defaultAvatar} alt="avatar" />
                </div>
                {preview.senderName && (
                  <div className="sender-id-container">
                    <span className="sender-id">{preview.senderName}</span>
                    <ChevronRightIcon />
                  </div>
                )}
                <div className="message-wrap">
                  <div className="current-time">
                    {t('sms_page.quick_send.preview.today')}{' '}
                    {momentLocale().format('h:mm a')}
                  </div>
                  {preview.msg && <div className="chat">{preview.msg}</div>}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default QuickSend;
